import { headingLevels } from './heading-levels'
import { continuesWrappedLine, WRAP_RATIO } from './normalizePastedText'

const HEADING_RATIO = 1.15
const MAX_WRAPPED_LINE = 160

type Block =
  | { kind: 'blank' }
  | { kind: 'body'; html: string; text: string; wrapped?: boolean }
  | { kind: 'heading'; html: string; size: number }

const readFontSizes = (doc: Document) => {
  const sizes = new Map<string, number>()
  const css = [...doc.querySelectorAll('style')]
    .map(s => s.textContent)
    .join('\n')

  for (const [, selector, rule] of css.matchAll(/([\w.]+)\s*\{([^}]*)\}/g)) {
    const size = rule?.match(/font(?:-size)?:[^;]*?([\d.]+)px/)?.[1]
    if (selector && size) sizes.set(selector, Number(size))
  }
  return sizes
}

const sizeOf = (element: Element, sizes: Map<string, number>) => {
  const tag = element.tagName.toLowerCase()
  return sizes.get(`${tag}.${element.className}`)
}

/**
 * Splits a paragraph wherever its text changes font size, because macOS glues
 * a heading onto the end of the line above it as a differently-sized span.
 */
const paragraphRuns = (paragraph: Element, sizes: Map<string, number>) => {
  const paragraphSize = sizeOf(paragraph, sizes) ?? 0
  const runs: { size: number; nodes: Node[] }[] = []

  paragraph.childNodes.forEach(node => {
    if (!node.textContent?.trim()) return

    const ownSize = node instanceof Element ? sizeOf(node, sizes) : undefined
    const size = ownSize ?? paragraphSize
    const content = ownSize ? [...node.childNodes] : [node]
    const last = runs[runs.length - 1]

    if (last && last.size === size) last.nodes.push(...content)
    else runs.push({ size, nodes: content })
  })

  return runs.map(({ size, nodes }) => {
    const wrapper = paragraph.ownerDocument.createElement('span')
    nodes.forEach(node => wrapper.appendChild(node.cloneNode(true)))
    return {
      size,
      html: wrapper.innerHTML.trim(),
      text: wrapper.textContent ?? '',
    }
  })
}

const dominantSize = (runs: { size: number; text: string }[]) => {
  const weight = new Map<number, number>()
  runs.forEach(({ size, text }) =>
    weight.set(size, (weight.get(size) ?? 0) + text.length)
  )
  return [...weight].sort((a, b) => b[1] - a[1])[0]?.[0] ?? 0
}

/**
 * Joins the lines of each wrapped paragraph and puts an empty line after it,
 * because the clipboard keeps no paragraph spacing.
 */
const joinWrappedLines = (blocks: Block[]) => {
  const lengths = blocks.flatMap(b =>
    b.kind === 'body' ? [b.text.length] : []
  )
  const longest = Math.max(0, ...lengths)
  if (longest > MAX_WRAPPED_LINE) return blocks

  const joined: Block[] = []
  blocks.forEach((block, index) => {
    const previous = blocks[index - 1]
    const last = joined[joined.length - 1]

    if (
      block.kind === 'body' &&
      previous?.kind === 'body' &&
      last?.kind === 'body' &&
      continuesWrappedLine(previous.text, block.text, longest * WRAP_RATIO)
    ) {
      last.html += ` ${block.html}`
      last.wrapped = true
    } else joined.push(block.kind === 'body' ? { ...block } : block)
  })

  return joined.flatMap<Block>((block, index) => {
    const next = joined[index + 1]
    const spaced =
      block.kind === 'body' && block.wrapped && next && next.kind !== 'blank'
    return spaced ? [block, { kind: 'blank' }] : [block]
  })
}

const blocksToHtml = (blocks: Block[]) => {
  const levels = headingLevels(
    blocks.flatMap(b => (b.kind === 'heading' ? [b.size] : []))
  )

  return blocks
    .map((block, index) => {
      if (block.kind === 'blank') return '<p><br></p>'
      if (block.kind === 'body') return `<p>${block.html}</p>`

      const level = levels.get(block.size)
      const spacer =
        index > 0 && blocks[index - 1]?.kind !== 'blank' ? '<p><br></p>' : ''
      return `${spacer}<h${level}>${block.html}</h${level}>`
    })
    .join('')
}

/**
 * macOS Preview copies PDF text as "Cocoa HTML Writer" markup: one <p> per
 * visual line, with font sizes kept in a <style> block that Quill never reads.
 * Rebuilding it here turns larger-than-body lines back into headings and joins
 * wrapped lines, so the paste keeps the PDF's structure.
 *
 * Returns null for any other HTML, which Quill then converts as usual.
 */
export function normalizeCocoaHtml(html: string): string | null {
  const doc = new DOMParser().parseFromString(html, 'text/html')
  const generator = doc.querySelector('meta[name="Generator"]')
  if (generator?.getAttribute('content') !== 'Cocoa HTML Writer') return null

  const paragraphs = [...doc.body.children]
  if (paragraphs.some(el => el.tagName !== 'P')) return null

  const sizes = readFontSizes(doc)
  const lines = paragraphs.map(p => paragraphRuns(p, sizes))
  const bodySize = dominantSize(lines.flat())
  if (!bodySize) return null

  const blocks: Block[] = lines.flatMap<Block>(runs => {
    if (runs.length === 0) return [{ kind: 'blank' }]

    return runs.map(({ size, html, text }) =>
      size >= bodySize * HEADING_RATIO
        ? { kind: 'heading', html, size }
        : { kind: 'body', html, text: text.trim() }
    )
  })

  return blocksToHtml(joinWrappedLines(blocks))
}
